import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import i18n from '../i18n';
import { languageByLocale, SUPPORTED_LANGUAGES } from '../config/languages';
import type { AuthUser } from '../utils/auth';
import {
  getStoredPortalLanguage,
  LANGUAGE_CHANGE_EVENT,
  notifyPortalLanguageChange,
  setStoredPortalLanguage,
} from '../utils/portalLanguage';

interface LanguageSelectorProps {
  currentUser?: AuthUser | null;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ currentUser = null }) => {
  const [language, setLanguage] = useState('en');
  const [isHovered, setIsHovered] = useState(false);

  const updateLanguage = () => {
    const storedLang = getStoredPortalLanguage(currentUser?.id);
    setLanguage(storedLang);
    i18n.changeLanguage(storedLang);
  };

  useEffect(() => {
    updateLanguage();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'language' || e.key === `linkPortalLanguage:${currentUser?.id}`) {
        updateLanguage();
      }
    };

    const handleLanguageChange = () => {
      updateLanguage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [currentUser?.id]);

  const handleChange = (newLang: string) => {
    const normalizedLanguage = setStoredPortalLanguage(newLang, currentUser?.id);
    setLanguage(normalizedLanguage);
    i18n.changeLanguage(normalizedLanguage);
    notifyPortalLanguageChange();
    setIsHovered(false);
  };

  const selectedLanguage = languageByLocale(language);
  const otherLanguages = SUPPORTED_LANGUAGES.filter((lang) => lang.code !== language);

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
        bgcolor: '#fff',
          minWidth: '6rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        overflow: 'visible',
        borderRadius: isHovered ? '22px 22px 0 0' : '22px',
        border: '1px solid #330662',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: '8px 12px',
          bgcolor: '#fff',
          borderRadius: 'inherit',
        }}
      >
        <Box
          component="img"
          src={selectedLanguage.flag}
          alt={selectedLanguage.label}
          sx={{ width: 24, height: 24, borderRadius: '50%' }}
        />
        <Box
          component="span"
          sx={{
            fontSize: 16,
            fontWeight: 'bold',
            color: '#330662',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {selectedLanguage.shortLabel}
          <Box component="span" sx={{ width: 15, textAlign: 'center', fontSize: 20, pb: '3.5px' }}>
            {isHovered ? '-' : '+'}
          </Box>
        </Box>
      </Box>

      {isHovered && (
        <Box
          component="ul"
          sx={{
            position: 'absolute',
            top: '100%',
            left: -1,
            right: -1,
            bgcolor: '#fff',
            listStyle: 'none',
            p: 0,
            m: 0,
            zIndex: 10,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '0 0 22px 22px',
            border: '1px solid #330662',
            borderTop: 'none',
          }}
        >
          {otherLanguages.map((lang) => (
            <Box
              component="li"
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: '8px 12px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
                color: '#330662',
                transition: 'background 0.2s ease-in-out',
                borderRadius: '0 0 22px 22px',
                '&:hover': { bgcolor: '#f0f0f0' },
              }}
            >
              <Box
                component="img"
                src={lang.flag}
                  alt={lang.label}
                sx={{ width: 24, height: 24, borderRadius: '50%' }}
              />
              {lang.shortLabel}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LanguageSelector;
